import { Box, Divider, Drawer, FormControlLabel, Radio, RadioGroup, Switch, Toolbar, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '../components/ui/IconButton';
import { ThemeMode } from '../context/ThemeContext';
import { useNotificationPreferencesMutation } from '../hooks/mutations/useNotificationPreferencesMutation';
import { useNotificationPreferencesQuery } from '../hooks/queries/useNotificationPreferencesQuery';
import { useTheme } from '../hooks/useTheme';
import { NotificationPreferences } from '../services/apiSLTP/notifications';
import { capitalizeFirstLetter } from '../utils/capitalize';

interface SettingsPanelProps {
    open: boolean;
    onClose: () => void;
}

type PrefKey = keyof Omit<NotificationPreferences, '_id' | 'userId'>;

const NOTIFICATION_CHANNELS: { key: PrefKey; label: string }[] = [
    { key: 'planning', label: 'Planning' },
    { key: 'gnr', label: 'Carburant (GNR)' },
    { key: 'needs', label: 'Besoins' },
    { key: 'reports', label: 'Rapports' },
    { key: 'forms', label: 'Formulaires QHSE' },
    { key: 'securityPassport', label: 'Passeport sécurité' },
];

const SettingsPanel: React.FC<SettingsPanelProps> = ({ open, onClose }) => {
    const { mode, setThemeMode, muiTheme } = useTheme();
    const { i18n, t } = useTranslation();
    const { data: prefs } = useNotificationPreferencesQuery();
    const { mutate: updatePrefs } = useNotificationPreferencesMutation();

    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    const handleLangChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        i18n.changeLanguage(event.target.value);
    };

    const handleToggle = (key: PrefKey, value: boolean) => {
        updatePrefs({ [key]: value });
    };

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? open : true}
            onClose={onClose}
            anchor="right"
            ModalProps={{ keepMounted: true }}
            sx={{
                width: open || isMobile ? '240px' : '0px',
                transition: muiTheme.transitions.create('width', {
                    easing: muiTheme.transitions.easing.sharp,
                    duration: muiTheme.transitions.duration.enteringScreen,
                }),
                flexShrink: 0,
                position: 'sticky',
                '& .MuiDrawer-paper': {
                    width: open || isMobile ? '240px' : '0px',
                    transition: muiTheme.transitions.create('width', {
                        easing: muiTheme.transitions.easing.sharp,
                        duration: muiTheme.transitions.duration.enteringScreen,
                    }),
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    border: 0,
                    backgroundColor: isMobile ? undefined : '#000',
                },
            }}
        >
            {!isMobile && <Toolbar />}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    gap: 2,
                    padding: 1,
                    borderRadius: 2,
                    backgroundColor: isMobile ? undefined : muiTheme.palette.background.paper,
                    marginRight: '10px',
                    marginBottom: '10px',
                    overflowY: 'auto',
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{capitalizeFirstLetter(t('settings'))}</Typography>
                    <IconButton iconName="close" onClick={onClose} />
                </Box>

                <Divider />
                <Box>
                    <Typography variant="subtitle1">{capitalizeFirstLetter(t('theme'))}</Typography>
                    <RadioGroup value={mode} onChange={(e, value) => setThemeMode(value as ThemeMode)}>
                        <FormControlLabel value="dark" control={<Radio />} label={capitalizeFirstLetter(t('dark'))} />
                        <FormControlLabel value="light" control={<Radio />} label={capitalizeFirstLetter(t('light'))} />
                    </RadioGroup>
                </Box>

                <Divider />
                <Box>
                    <Typography variant="subtitle1">{capitalizeFirstLetter(t('language'))}</Typography>
                    <RadioGroup value={i18n.language} onChange={handleLangChange}>
                        <FormControlLabel value="fr" control={<Radio />} label="Français" />
                        <FormControlLabel value="en" control={<Radio />} label="English" disabled />
                    </RadioGroup>
                </Box>

                <Divider />
                <Box>
                    <Typography variant="subtitle1">Notifications</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                        {NOTIFICATION_CHANNELS.map(({ key, label }) => (
                            <FormControlLabel
                                key={key}
                                label={<Typography variant="body2">{label}</Typography>}
                                control={
                                    <Switch
                                        size="small"
                                        checked={prefs?.[key] ?? true}
                                        onChange={(e) => handleToggle(key, e.target.checked)}
                                        disabled={!prefs}
                                    />
                                }
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default SettingsPanel;
